import {useEffect, useState} from 'react';

import {useGeolocation} from '@/components/global/providers/GeolocationProvider';
import {useGeocodeCoordinatesQueryLazyQuery} from '@/graphql/types';
import useTracking from '@/hooks/useTracking';
import {geocodedAddressResultToString} from '@/utils/geocodedAddressResult';
import {type AddressSearchAutocompleteSuffixProps} from './types';

type UseAddressGeolocationArgs = Pick<
  AddressSearchAutocompleteSuffixProps,
  'geocodedAddressResultHelpers' | 'inputHelpers'
> & {
  onAddressFilled?: () => void;
};

const useAddressGeolocation = ({
  geocodedAddressResultHelpers,
  inputHelpers,
  onAddressFilled,
}: UseAddressGeolocationArgs) => {
  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {track, trackClick} = useTracking();
  const [geocodeCoordinatesQuery, {error: queryError}] = useGeocodeCoordinatesQueryLazyQuery();
  const {getCurrentPosition} = useGeolocation();

  const handleGeolocationSuccess = async (position: GeolocationPosition) => {
    setIsLoading(true);

    track('precise-location-granted', {
      page: window.location.pathname,
      url: window.location.href,
    });

    const {latitude, longitude} = position.coords;
    const {data} = await geocodeCoordinatesQuery({variables: {latitude, longitude}});
    const firstGeocodedAddressResult = data?.geocodedCoordinates?.[0];
    setIsLoading(false);

    if (firstGeocodedAddressResult) {
      const addressString = geocodedAddressResultToString(firstGeocodedAddressResult);
      inputHelpers.setValue(addressString);
      geocodedAddressResultHelpers.setValue(firstGeocodedAddressResult);
      onAddressFilled?.();

      track('precise-location-address-filled', {
        misc_json: JSON.stringify({address_string: addressString}),
        page: window.location.pathname,
        url: window.location.href,
      });
    }
  };

  const handleGeolocationError = (positionError: GeolocationPositionError) => {
    track('precise-location-denied', {
      misc_json: JSON.stringify({error_message: positionError.message}),
      page: window.location.pathname,
      url: window.location.href,
    });

    setIsLoading(false);
    setHasErrors(true);
  };

  const getGeolocation = () => {
    setIsLoading(true);
    setHasErrors(false);
    trackClick('precise-location-target');
    getCurrentPosition(handleGeolocationSuccess, handleGeolocationError);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (queryError) setHasErrors(true);
  }, [queryError]);

  return {getGeolocation, hasErrors, isLoading, setHasErrors};
};

export default useAddressGeolocation;
